import React, { useState, useEffect, useRef } from 'react';
import {
  IonContent,
  IonPage,
  IonRefresher,
  IonRefresherContent,
  IonInfiniteScroll,
  IonInfiniteScrollContent,
} from '@ionic/react';
import { motion } from 'framer-motion';
import toast from 'react-hot-toast';
import { Stack } from '@mui/material';
import useFetch, { CachePolicies } from 'use-http';
import LoadingSpinner from 'components/generic/LoadingSpinner';
import { useHistory } from 'react-router';
import { useSelector, useDispatch } from 'react-redux';
import {
  updateActiveFilter,
  feedSelector,
  updateFeedFilters,
  setFeedPosts,
  appendFeedPosts,
  setShouldRestoreFeedPosition,
  setTotalPostLimit,
  updateLastPostAnchor,
} from '../redux/slices/feed';
import ReportCueCard from '../components/ReportCueCard';

import Feed from '../components/Feed';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { useAuthContext } from '../providers/auth-provider';
import { useAppState } from '../providers/app-state/app-state-provider';
import FilterBar from '../common/atoms/filterBar/FilterBar';
import DesktopFilterBar from '../common/atoms/desktopFilterBar/DesktopFilterBar';
import appStrings from '../common/app_strings';
import FeedError from '../components/FeedError';
import AlertCard from '../common/atoms/alertCard/AlertCard';
import CardAtom from '../common/atoms/card/CardAtom';
import AffinityNetwork from './feed/affinityNetwork/AffinityNetwork';
import AffinityMemberList from './feed/affinityNetwork/AffinityMemberList';
import GrowBanner from '../common/molecules/GrowBanner/GrowBanner';

const {
  feed: { errors },
  wholeMe: { wholeMeAlertCard },
} = appStrings;

const allPostFilter = {
  type: null,
  value: 'all',
  label: 'All Posts',
  icon: '/assets/all-post-filter-icon.svg',
};

const UnifiedUserFeed = () => {
  const mountedRef = useRef(true);
  const loadingRef = useRef(false);
  const contentRef = useRef();
  const timeoutRef = useRef(null);
  const {
    activeFilter, posts, shouldRestoreFeedPosition, totalPostLimit, lastPostAnchor,
  } = useSelector(feedSelector);
  const dispatch = useDispatch();
  const [loading, setIsLoading] = useState(false);
  const [isInfiniteDisabled, setInfiniteDisabled] = useState(false);
  const [filterMeta, setFilterMeta] = useState([]);
  const [showFeed, setShowFeed] = useState(false);
  const [showFeedError, setShowFeedError] = useState(false);
  const [showWholeMeAlert, setShowWholeMeAlert] = useState(false);
  const [affinityListRole, setAffinityListRole] = useState(null);
  const [isAffinityListLoaded, setIsAffinityListLoaded] = useState(false);
  // Two separate useFetch instances so concurrent filter + post fetches
  // don't share response state. A single useFetch only tracks one
  // in-flight request at a time; under StrictMode (2 mounts) we fire 4
  // concurrent requests and the responses collide — the filter call's
  // `response` would be overwritten by the post call's, making
  // `filterData` look falsy and triggering "filters unavailable".
  const { get: getFilters, response: filtersResponse } = useFetch({ cachePolicy: CachePolicies.NO_CACHE });
  const { get, response } = useFetch({ cachePolicy: CachePolicies.NO_CACHE });

  const PER_PAGE = 10;
  const {
    user,
    reported,
    setReported,
    hasGivenFeedback,
    setHasGivenFeedback,
  } = useAuthContext();
  const {
    platform: { isNativePlatform },
    refreshPostTargets,
    elapsedPostTargetUpdate,
  } = useAppState();

  const showNativeFeed = filterMeta.length > 0 && !showFeedError && isNativePlatform;
  const showWebFeed = filterMeta.length > 0 && !showFeedError && !isNativePlatform;
  const showAffinityNetwork = activeFilter?.type === 'group';
  const showAffinityMemberList = showAffinityNetwork && affinityListRole;

  const setLoadingRef = (isLoading) => {
    loadingRef.current = isLoading;
    setIsLoading(loadingRef.current);
  };

  const buildFilterItemMeta = async () => {
    const filterData = await getFilters('/user-feed/filters');
    if (!mountedRef.current) return;
    if (filterData && filtersResponse.ok) {
      const filterList = [allPostFilter, ...filterData];
      setFilterMeta(filterList);
      dispatch(updateFeedFilters(filterList));
    } else if (filtersResponse.ok === false) {
      // Only toast on real server-side failures, not on aborted/superseded
      // requests where `ok` stays at its initial false-ish state.
      toast(errors.filtersUnavailable);
    }
  };

  const history = useHistory();

  const fetchPosts = async ({ filter = undefined, min, max }) => {
    try {
      const params = new URLSearchParams();
      params.append('range', `[${min},${max}]`);
      params.append('sort', '["created_at","DESC"]');

      if (filter && filter.value !== 'all') {
        params.append('feed_filter', `["${filter.type}",${filter.value}]`);
      }

      // return before request if component not mounted
      if (!mountedRef.current) return [];

      const res = await get(`/user-feed?${params.toString()}`);

      // if not mounted, return empty array before setting errors
      if (!mountedRef.current) return [];

      // If the request was superseded by a newer one (StrictMode mount #2,
      // or a fast filter change), use-http resolves the older promise with
      // `undefined` and leaves response.ok untouched. Treat that as a
      // silent no-op, not a feed error — otherwise every fresh /tabs/feed
      // load under StrictMode renders the error state.
      if (!res && !response.ok) {
        return [];
      }

      if (!res || res?.error) {
        if (mountedRef.current) {
          setShowFeedError(true);
        }
        const errorMessage = res?.error || 'Unknown error';
        console.error('Error fetching posts: ', errorMessage);
        return [];
      }

      const totalCount = parseInt(response?.headers?.get('x-total-count'), 10);

      if (totalCount !== totalPostLimit) {
        dispatch(setTotalPostLimit(totalCount));
      }

      return res;
    } catch (e) {
      if (mountedRef.current) {
        setShowFeedError(true);
      }
      console.error('Error fetching posts: ', e);
      return [];
    }
  };

  const scrollToTop = () => {
    contentRef.current?.scrollToTop(100);
  };

  const updateFeed = async ({ filter = undefined, refresh = false } = {}) => {
    if (refresh && !shouldRestoreFeedPosition) {
      dispatch(setFeedPosts([]));
      setShowFeed(false);
    }
    try {
      if (!loadingRef.current && mountedRef.current) {
        const max = refresh ? PER_PAGE - 1 : posts.length + PER_PAGE - 1;
        const min = refresh ? 0 : posts.length;

        timeoutRef.current = setTimeout(() => {
          if (mountedRef.current) {
            setLoadingRef(true);
          }
        }, 200);

        // return early if we need to restore the feed position and posts already exist in the store
        if (shouldRestoreFeedPosition && posts.length > 0) {
          return;
        }

        const newPosts = await fetchPosts({ filter, min, max });

        if (!mountedRef.current) return;

        clearTimeout(timeoutRef.current);
        if (refresh) {
          dispatch(setFeedPosts(newPosts));
          scrollToTop();
          setInfiniteDisabled(false);
        } else {
          dispatch(appendFeedPosts(newPosts));
        }
      }
    } catch (e) {
      if (mountedRef.current) {
        console.error('Error in updateFeed: ', e);
      }
    } finally {
      if (mountedRef.current) {
        setLoadingRef(false);
        clearTimeout(timeoutRef.current);
        setShowFeed(true);
      }
    }
  };

  const onScroll = async (ev) => {
    if (posts.length >= totalPostLimit || posts.length >= 1000) {
      setInfiniteDisabled(true);
    } else {
      await updateFeed({ filter: activeFilter });
    }
    ev.target.complete();
  };

  const onIonRefresh = async (event) => {
    await updateFeed({ filter: activeFilter, refresh: true });
    event.detail.complete();
  };

  const filterCallBack = async (filter) => {
    dispatch(updateActiveFilter(filter));
    await updateFeed({ filter, refresh: true });
  };

  const handleReportedAlertClose = () => {
    localStorage.setItem('isFlagged', 'false');
    setReported(false);
  };

  const handleFeedbackClose = () => {
    setHasGivenFeedback(false);
  };

  const handleWholeMeAlertClose = () => {
    setShowWholeMeAlert(false);
  };

  const goToWholeMe = () => {
    history.push('/wholeme/portrait');
  };

  const saveScrollPosition = (elementId = null) => {
    if (!elementId) {
      return;
    }
    dispatch(updateLastPostAnchor(elementId));
  };

  const renderDefaultFeedContent = () => (
    <IonContent ref={contentRef}>
      <IonRefresher slot="fixed" onIonRefresh={onIonRefresh}>
        <IonRefresherContent />
      </IonRefresher>
      {loading && !showFeed ? (
        <LoadingSpinner />
      ) : (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.3, delay: 0.5 }}
            key={activeFilter.value}
          >
            {hasGivenFeedback && (
            <CardAtom
              img="assets/bulb-feedback.svg"
              header="Thanks for your feedback!"
              body="Our support team will be contact soon. In the meantime, explore our community!"
              containerStyles={{
                p: '20px',
                mx: 3,
                my: 1,
                bgcolor: '#E5E5DE',
              }}
              headerStyles={{ color: '#03295F' }}
              bodyStyles={{ color: '#2d2d2d', fontSize: 13 }}
              imgStyles={{ maxWidth: '85px', paddingTop: '16px' }}
              hasDismissableIcon={false}
              dismissCallback={handleFeedbackClose}
            />
            )}
            <GrowBanner />
            {showWholeMeAlert && !showFeedError && (
              <Stack
                sx={{
                  width: 'fit-content',
                  mt: 1,
                  ml: '25px',
                }}
              >
                <AlertCard
                  header={wholeMeAlertCard.header}
                  body={wholeMeAlertCard.body}
                  imageSrc="/assets/wholeMeCtaImg.svg"
                  altImgText="Complete your profile!"
                  onCardClickCallBack={goToWholeMe}
                  onCloseCallBack={handleWholeMeAlertClose}
                />
              </Stack>
            )}
            {showFeedError && posts.length < 1 && (
              <Stack
                sx={{
                  paddingLeft: '24px',
                  paddingRight: '24px',
                  paddingTop: '40px',
                }}
              >
                <FeedError />
              </Stack>
            )}
            {posts.length > 0 && (
              <Feed
                posts={posts}
                onChangeCallBack={filterCallBack}
                filterMeta={filterMeta}
                saveScrollPosition={saveScrollPosition}
              />
            )}
          </motion.div>
          <IonInfiniteScroll
            onIonInfinite={onScroll}
            threshold="300px"
            disabled={isInfiniteDisabled}
          >
            <IonInfiniteScrollContent loadingSpinner="crescent" />
          </IonInfiniteScroll>
        </>
      )}
    </IonContent>
  );

  useEffect(() => {
    if (elapsedPostTargetUpdate > 10) {
      refreshPostTargets();
    }
  }, []);

  useEffect(() => {
    mountedRef.current = true;

    return () => {
      mountedRef.current = false;
      // NOTE: do not call abort(). The mountedRef guards above already
      // drop stale results without triggering use-http's non-ok resolve
      // path that StrictMode dev double-invoke would otherwise hit.
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      loadingRef.current = false;
      setIsLoading(false);
      setInfiniteDisabled(false);
      setFilterMeta([]);
      setShowFeedError(false);
      setShowFeed(false);
      setShowWholeMeAlert(false);
      setAffinityListRole(null);
      setIsAffinityListLoaded(false);
    };
  }, []);

  useEffect(() => {
    let isCancelled = false;

    const initializeFeed = async () => {
      if (user && !isCancelled) {
        buildFilterItemMeta();
        await updateFeed({ filter: activeFilter, refresh: true });
      }
    };

    initializeFeed();

    return () => {
      isCancelled = true;
    };
  }, [user]);

  // for restoring scroll position when the user navigates back to the feed from comments page
  useEffect(() => {
    const restoreScrollPosition = async () => {
      if (posts.length > 0 && shouldRestoreFeedPosition && contentRef.current) {
        const postElement = document.getElementById(`post-${lastPostAnchor}`);
        if (postElement) {
          // Use IntersectionObserver to scroll anchored post into view.
          const observer = new IntersectionObserver(
            (entries) => {
              const [entry] = entries;
              // If the post is not fully visible, scroll it into view
              if (!entry.isIntersecting || entry.intersectionRatio < 0.8) {
                postElement.scrollIntoView({ block: 'center' });
              }
              observer.disconnect();
            },
            {
              root: contentRef.current,
              threshold: 0.8, // Trigger when 80% of the post is visible
              rootMargin: '0px', // No margin around the viewport
            },
          );

          observer.observe(postElement);

          // Fallback if the observer doesn't trigger/post still wasn't scrolled into view
          setTimeout(() => {
            observer.disconnect();
            postElement.scrollIntoView({ block: 'center' });
          }, 400);
        }
        dispatch(setShouldRestoreFeedPosition(false));
      }
    };
    restoreScrollPosition();
  }, [posts.length, shouldRestoreFeedPosition]);

  useEffect(() => () => {
    if (hasGivenFeedback) {
      setHasGivenFeedback(false);
    }
  }, []);

  return (
    <IonPage style={{ background: '#f3f3ed' }}>
      <Header />
      {showNativeFeed && (
        <Stack
          sx={{
            whiteSpace: 'nowrap',
            overflow: 'auto',
            mt: '15px',
            pb: '6px',
            ml: '24px',
            mr: '24px',
          }}
        >
          <FilterBar
            disableClickEventsOverrideRef={loadingRef}
            filterMeta={filterMeta}
            onChangeCallBack={filterCallBack}
          />
        </Stack>
      )}
      {showWebFeed && (
        <DesktopFilterBar
          disableClickEventsOverrideRef={loadingRef}
          filterMeta={filterMeta}
          onChangeCallBack={filterCallBack}
        />
      )}
      {showAffinityNetwork && (
        <div style={{ borderBottom: !affinityListRole && 'solid 2px #E5E5DD' }}>
          <AffinityNetwork
            role={affinityListRole}
            setRole={setAffinityListRole}
            isLoadComplete={isAffinityListLoaded}
          />
        </div>
      )}
      {showAffinityMemberList ? (
        <AffinityMemberList
          role={affinityListRole}
          setIsLoadComplete={setIsAffinityListLoaded}
        />
      ) : renderDefaultFeedContent()}
      {reported && !showFeedError && user?.role !== 'admin' && (
        <ReportCueCard onClickCallBack={handleReportedAlertClose} />
      )}
      <Footer />
    </IonPage>
  );
};

export default UnifiedUserFeed;
